import { sb } from '../../../supabase.ts';
import { escapeHtml } from '../../../main.ts';

interface SubjectRow {
  subject: string;
  cq: string;
  mcq: string;
  practical: string;
  total: string;
  gpa: string;
}

export async function renderOtherSchoolResultDetails9Page(app: HTMLElement): Promise<void> {
  const tableNameExam = 'exam_9_10_other_school';

  // Parse positional and query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const search = window.location.search;
  let targetIID = urlParams.get('iid') || '';
  let isShortView = false;

  if (search && search.includes('&')) {
    const cleanSearch = search.startsWith('?') ? search.substring(1) : search;
    const parts = cleanSearch.split('&');
    // Ensure it is a positional check by looking for presence of '=' in first part
    if (parts[0] && !parts[0].includes('=')) {
      targetIID = decodeURIComponent(parts[0]);
      isShortView = true;
    }
  }

  // Setup initial loading screen
  app.innerHTML = `
    <div style="background: #f0f2f5; min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: 'Inter', sans-serif;">
      <div style="text-align: center; padding: 60px; color: #475569;">
        <div style="border: 4px solid #f3f4f6; border-top: 4px solid #1e3a8a; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 16px; font-weight: 600;">🔄 Loading detailed report card...</p>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;

  // Clean raw header values
  function cleanHeaderKey(h: string): string {
    return String(h || '')
      .replace(/\*/g, '')
      .replace(/"/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  // Safe accessor helper
  function getStudentValueByHeader(student: any, hdr: string): string {
    if (!hdr) return '';
    const original = String(hdr);
    const variants = [
      cleanHeaderKey(original),
      cleanHeaderKey(original.replace(/\s*_\s*/g, '_')),
      cleanHeaderKey(original.replace(/\s{2,}/g, ' ')),
      cleanHeaderKey(original.replace(/\s*-\s*/g, '_'))
    ];
    
    const used = new Set<string>();
    for (const k of variants) {
      if (!k || used.has(k)) continue;
      used.add(k);
      const v = student[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  }

  function generateSubjectMapFromHeaders(hdrs: string[]): Record<string, Record<string, string>> {
    const map: Record<string, Record<string, string>> = {};
    if (!Array.isArray(hdrs)) return map;
    const norm = (s: string) => String(s || '').replace(/\*/g, '').trim();

    const subjects = new Set<string>();
    hdrs.forEach(hRaw => {
      const h = norm(hRaw);
      const m = h.match(/^(.*?)(_CQ|_WRITTEN|_MCQ|_CA|_Practical|_Total|_GPA)$/i);
      if (m) subjects.add(m[1]);
    });

    subjects.forEach(subject => {
      const find = (suffixes: string[]) => hdrs.find(h => {
        const hh = norm(h);
        return suffixes.some(s => hh.endsWith(s) && hh.startsWith(subject));
      }) || '';

      map[subject] = {
        CQ: find(['_CQ', '_WRITTEN']),
        MCQ: find(['_MCQ']),
        CA: find(['_CA']),
        Practical: find(['_Practical']),
        Total: find(['_Total']),
        GPA: find(['_GPA'])
      };
    });

    return map;
  }

  function pickVal(row: any, candidates: string[]): string {
    for (const k of candidates) {
      const v = row[k] ?? row[k.toLowerCase()] ?? row[k.toUpperCase()];
      if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  }

  if (!targetIID) {
    app.innerHTML = `
      <div style="text-align: center; padding: 60px; color: #d73a49; fontFamily: 'Inter', sans-serif;">
        <p style="font-size: 18px; font-weight: 600; marginBottom: 20px;">❌ No Student ID (iid) was specified in the URL.</p>
        <a href="/other_school/result/list_9" style="display: inline-block; padding: 10px 20px; background: #1e3a8a; color: #fff; text-decoration: none; border-radius: 8px; font-weight: 600;">← Back to List</a>
      </div>
    `;
    return;
  }

  try {
    const { data, error } = await sb.from(tableNameExam).select('*').eq('iid', targetIID).limit(1);
    if (error) throw error;

    if (!data || data.length === 0) {
      app.innerHTML = `
        <div style="text-align: center; padding: 60px; color: #d73a49; fontFamily: 'Inter', sans-serif;">
          <p style="font-size: 18px; font-weight: 600; marginBottom: 20px;">❌ Result not found for student IID "${escapeHtml(targetIID)}"</p>
          <a href="/other_school/result/list_9" style="display: inline-block; padding: 10px 20px; background: #1e3a8a; color: #fff; text-decoration: none; border-radius: 8px; font-weight: 600;">← Back to List</a>
        </div>
      `;
      return;
    }

    const examData = data[0];
    const headers = Object.keys(examData);

    // Normalize row values
    const student: Record<string, any> = {};
    headers.forEach(h => {
      const key = cleanHeaderKey(h);
      student[key] = examData[h] === null ? '' : String(examData[h]).trim();
    });

    const mobile = pickVal(examData, ['mobile', 'father_mobile', 'phone']);
    const gpaFinal = examData.gpa_final !== null && examData.gpa_final !== undefined ? String(examData.gpa_final) : '—';
    const remark = examData.remark !== null && examData.remark !== undefined ? String(examData.remark) : '—';
    const classRank = examData.class_rank !== null && examData.class_rank !== undefined ? String(examData.class_rank) : '—';

    // Parse positional URL verification QR link
    const qrBase = 'http://fenimodel.netlify.app/z_others/result/qr_d.html';
    const qrURL = `${qrBase}?IID=${encodeURIComponent(targetIID)}`;
    const qrImgSrc = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrURL)}&margin=1`;

    if (isShortView) {
      app.innerHTML = `
        <div style="background: #f0f2f5; min-height: 100vh; padding: 40px 10px; display: flex; align-items: center; justify-content: center; font-family: 'Inter', sans-serif;">
          <div style="background: #fff; max-width: 500px; width: 100%; padding: 35px 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.06); border-radius: 24px; border: 1px solid #e2e8f0; position: relative; text-align: center;">
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
              .verified-badge {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                background: #dcfce7;
                color: #15803d;
                padding: 6px 16px;
                border-radius: 50px;
                font-size: 11px;
                font-weight: 800;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 20px;
              }
              .info-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 12px;
                background: #f8fafc;
                padding: 20px;
                border-radius: 16px;
                border: 1px solid #e2e8f0;
                margin: 20px 0;
                text-align: left;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                font-size: 13px;
                border-bottom: 1px dashed #e2e8f0;
                padding-bottom: 8px;
              }
              .info-row:last-child {
                border-bottom: none;
                padding-bottom: 0;
              }
              .metric-card {
                background: #eff6ff;
                border: 1px solid #bfdbfe;
                border-radius: 16px;
                padding: 16px;
                margin-top: 15px;
              }
              .metric-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-top: 10px;
              }
              .metric-item {
                background: #fff;
                border: 1px solid #e2e8f0;
                border-radius: 12px;
                padding: 10px;
                text-align: center;
              }
            </style>

            <div class="verified-badge">
              ✅ VERIFIED RESULT
            </div>

            <div style="margin-bottom: 16px;">
              <img
                src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/tFCNijuby2oapSCgvrQ8/pub/aT4p92D1bGZMP8sh1wZN.png"
                alt="FMHS"
                style="display: block; max-width: 100%; height: auto; margin: 0 auto;"
                onerror="this.src='/pad.png'"
              />
            </div>

            <div style="color: #fff; background: #e11d48; display: inline-block; padding: 6px 20px; font-size: 13px; font-weight: 800; border-radius: 50px; text-transform: uppercase; margin-bottom: 20px;">
              ${escapeHtml(examData.exam_name_year || 'Examination')}
            </div>

            <h2 style="font-size: 20px; font-weight: 900; color: #0f172a; text-transform: uppercase; margin-bottom: 4px;">
              ${escapeHtml(examData.student_name_en || '-')}
            </h2>

            <div class="info-grid">
              <div class="info-row">
                <span style="font-weight: 600; color: #64748b;">Student ID:</span>
                <span style="font-weight: 800; color: #1e293b;">${escapeHtml(String(examData.iid || '-'))}</span>
              </div>
              <div class="info-row">
                <span style="font-weight: 600; color: #64748b;">Class:</span>
                <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.class_2025 || '-')}</span>
              </div>
              <div class="info-row">
                <span style="font-weight: 600; color: #64748b;">Section:</span>
                <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.section_2025 || '-')}</span>
              </div>
              <div class="info-row">
                <span style="font-weight: 600; color: #64748b;">Roll No:</span>
                <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.roll_2025 || '-')}</span>
              </div>
              <div class="info-row">
                <span style="font-weight: 600; color: #64748b;">Father's Name:</span>
                <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.father_name_en || 'N/A')}</span>
              </div>
            </div>

            <div class="metric-card">
              <div style="font-size: 11px; font-weight: 800; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.5px;">
                Result Summary
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; background: #fff; padding: 12px 16px; border-radius: 12px; border: 1px solid #bfdbfe;">
                <span style="font-size: 12px; font-weight: 800; color: #1e3a8a;">GPA (FINAL)</span>
                <span style="font-size: 20px; font-weight: 900; color: ${gpaFinal === 'F' || gpaFinal === '0' || gpaFinal === '0.00' ? '#ef4444' : '#10b981'};">${gpaFinal}</span>
              </div>

              <div class="metric-grid">
                <div class="metric-item">
                  <div style="font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase;">Total Mark</div>
                  <div style="font-size: 15px; font-weight: 800; color: #0f172a; margin-top: 4px;">${examData.total_mark !== null && examData.total_mark !== undefined ? parseFloat(examData.total_mark).toFixed(1) : '—'}</div>
                </div>
                <div class="metric-item">
                  <div style="font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase;">Avg Mark</div>
                  <div style="font-size: 15px; font-weight: 800; color: #0f172a; margin-top: 4px;">${examData.average_mark !== null && examData.average_mark !== undefined ? parseFloat(examData.average_mark).toFixed(1) : '—'}</div>
                </div>
                <div class="metric-item">
                  <div style="font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase;">Remark</div>
                  <div style="font-size: 13px; font-weight: 800; color: ${String(remark).toLowerCase().includes('fail') ? '#ef4444' : '#10b981'}; margin-top: 4px;">${remark}</div>
                </div>
                <div class="metric-item">
                  <div style="font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase;">Class Rank</div>
                  <div style="font-size: 15px; font-weight: 800; color: #0f172a; margin-top: 4px;">${classRank}</div>
                </div>
              </div>
            </div>

            <div style="margin-top: 25px; font-size: 10px; color: #94a3b8; font-weight: 600;">
              © ${new Date().getFullYear()} Feni Model High School. All rights reserved.
            </div>
          </div>
        </div>
      `;
      return;
    }

    // Process subject map rows for standard view
    const subjMap = generateSubjectMapFromHeaders(headers);
    const subjectsList: SubjectRow[] = [];

    Object.entries(subjMap).forEach(([subject, cols]) => {
      const cq = getStudentValueByHeader(student, cols.CQ);
      const mcq = getStudentValueByHeader(student, cols.MCQ);
      const ca = getStudentValueByHeader(student, cols.CA);
      let practical = getStudentValueByHeader(student, cols.Practical);
      const total = getStudentValueByHeader(student, cols.Total);
      const gpa = getStudentValueByHeader(student, cols.GPA);

      // Helper: check empty or zero-ish
      const isEmptyOrZero = (val: string) => {
        const t = String(val || '').trim();
        if (t === '' || t === '-') return true;
        const n = Number(t);
        return !Number.isNaN(n) && n === 0;
      };

      const subjClean = subject.replace(/\*/g, '').toLowerCase();
      const isEnglish1 = /\benglish\b/.test(subjClean) && /\b(1|1st|first)\b/.test(subjClean);
      const isEnglish2 = /\benglish\b/.test(subjClean) && /\b(2|2nd|second)\b/.test(subjClean);
      const isEnglish = isEnglish1 || isEnglish2;

      if (!isEnglish) {
        if ((!practical || practical === '-') && total) {
          const n = (s: string) => isNaN(parseFloat(s)) ? 0 : parseFloat(s);
          const derived = n(total) - (n(cq) + n(mcq) + n(ca));
          if (derived > 0) practical = String(derived);
        }
      } else {
        practical = '';
      }

      const allBlankOrZero = [cq, mcq, ca, practical, total, gpa].every(isEmptyOrZero);
      if (allBlankOrZero) return; // Skip completely blank subject fields

      subjectsList.push({
        subject: subject.replace(/\*/g, ''),
        cq,
        mcq,
        practical: isEnglish ? '' : practical,
        total,
        gpa
      });
    });

    const classNameClean = String(examData.class_2025 || '').trim();
    const isClass6to8 = ['6', '7', '8', 'six', 'seven', 'eight', 'class_6', 'class_7', 'class_8'].some(c => classNameClean.toLowerCase().includes(c));
    const header1 = isClass6to8 ? 'WRITTEN' : 'CQ';
    const header2 = isClass6to8 ? 'CA' : 'MCQ';

    app.innerHTML = `
      <div style="background: #f0f2f5; min-height: 100vh; padding: 20px 10px;">
        <div style="font-family: 'Inter', sans-serif; background: #fff; max-width: 800px; margin: 0 auto; padding: 30px 40px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); border-radius: 16px; position: relative;">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
            
            .no-print { display: flex; gap: 8px; margin-bottom: 20px; }
            @media print { 
              @page { size: A4; margin: 10mm; }
              .no-print { display:none !important; } 
              body { background: #fff !important; margin: 0; padding: 0; }
              .main-container { 
                box-shadow: none !important; margin: 0 !important; width: 100% !important; 
                max-width: none !important; border-radius: 0 !important; padding: 0 !important;
                border: none !important;
              }
              .summary-bar { border: 1px solid #dbeafe !important; background: #f0f7ff !important; }
              
              /* Print scaling for A4 */
              .exam-title { 
                margin-bottom: 12px !important; font-size: 13px !important; 
                padding: 4px 20px !important;
              }
              .student-name { font-size: 18px !important; margin-bottom: 4px !important; }
              .student-details { margin-bottom: 12px !important; font-size: 12px !important; }
              .marks-table { margin-bottom: 12px !important; }
              .marks-table th, .marks-table td { padding: 8px 6px !important; font-size: 11px !important; }
              .summary-bar { margin-bottom: 15px !important; padding: 8px 10px !important; border-radius: 12px !important; }
              .summary-value { font-size: 14px !important; }
              .qr-section { margin-top: 10px !important; }
              .qr-box img { width: 90px !important; height: 90px !important; }
            }
            
            .header-title { color: #1e3a8a; font-size: 28px; font-weight: 800; text-align: center; margin-bottom: 4px; }
            .header-subtitle { color: #4b5563; font-size: 16px; text-align: center; margin-bottom: 12px; }
            .exam-title { 
              color: #fff; 
              background: #e11d48;
              display: inline-block;
              padding: 6px 30px;
              font-size: 16px; 
              font-weight: 800; 
              text-align: center; 
              margin: 0 auto 16px auto;
              border-radius: 50px;
              text-transform: uppercase;
              letter-spacing: 1px;
              box-shadow: 0 4px 10px rgba(225, 29, 72, 0.2);
            }
            .exam-title-container { text-align: center; }
            
            .student-name { font-size: 22px; font-weight: 900; color: #1e293b; margin-bottom: 6px; text-transform: uppercase; letter-spacing: -0.5px; }
            .student-details { font-size: 13px; color: #475569; line-height: 1.5; margin-bottom: 16px; }
            
            .marks-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 16px; 
              border-radius: 16px; 
              overflow: hidden; 
              border: 1px solid #e2e8f0;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03);
            }
            .marks-table th { 
              padding: 10px 8px; 
              font-size: 12px; 
              font-weight: 800; 
              color: #fff; 
              text-align: center; 
              background-color: #22c55e; /* Vibrant Green Header */
              text-transform: uppercase;
              border: 1px solid #e2e8f0;
            }
            .marks-table th.subject-col { 
              background-color: #1d82e2; /* Bright Blue Header */
              text-align: left; 
              width: 35%; 
              padding-left: 20px;
            }
            
            .marks-table td { 
              padding: 10px 8px; 
              font-size: 14px; 
              text-align: center; 
              background-color: #fffdf4; /* Creamy yellow background */
              color: #334155;
              border: 1px solid #e2e8f0; /* Light grid borders */
            }
            .marks-table td.subject-name { 
              color: #1d4ed8; /* Indigo/blue subject name */
              font-weight: 800; 
              text-align: left; 
              background-color: #eff6ff; /* Light pastel blue background for subject col */
              padding-left: 20px;
            }
            
            .summary-bar { 
              display: flex; justify-content: space-between; 
              background-color: #f0f7ff; padding: 12px 20px; 
              border-radius: 14px; margin-bottom: 20px;
              box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
              border: 1px solid #e0f2fe;
            }
            .summary-item { text-align: center; flex: 1; border-right: 1px solid #bae6fd; }
            .summary-item:last-child { border-right: none; }
            .summary-label { font-size: 10px; font-weight: 800; color: #0369a1; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
            .summary-value { font-size: 16px; font-weight: 900; color: #0f172a; }
            
            .qr-section { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 10px; }
            .qr-box { border: 1px solid #e2e8f0; padding: 8px; display: inline-block; background: #fff; border-radius: 12px; }
            .qr-text { font-size: 11px; color: #64748b; margin-bottom: 6px; }
            .scan-verify { font-size: 10px; text-align: center; margin-top: 4px; font-weight: 700; color: #475569; text-transform: uppercase; }

            /* PDF generation overrides to fit on one page */
            .generating-pdf {
              box-shadow: none !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 800px !important;
              border-radius: 0 !important;
              padding: 25px 35px !important;
              border: none !important;
            }
            .generating-pdf .no-print {
              display: none !important;
            }
            .generating-pdf .student-name {
              font-size: 20px !important;
              margin-bottom: 6px !important;
            }
            .generating-pdf .student-details {
              margin-bottom: 15px !important;
              font-size: 12.5px !important;
              gap: 8px 24px !important;
              padding-bottom: 10px !important;
            }
            .generating-pdf .marks-table {
              margin-bottom: 15px !important;
            }
            .generating-pdf .marks-table th,
            .generating-pdf .marks-table td {
              padding: 10px 10px !important;
              font-size: 12px !important;
            }
            .generating-pdf .summary-bar {
              margin-bottom: 18px !important;
              padding: 10px 16px !important;
              border-radius: 12px !important;
              border: 1px solid #dbeafe !important;
              background: #f0f7ff !important;
            }
            .generating-pdf .summary-value {
              font-size: 15px !important;
            }
            .generating-pdf .qr-section {
              margin-top: 15px !important;
            }
            .generating-pdf .qr-box img {
              width: 95px !important;
              height: 95px !important;
            }
            .generating-pdf .scan-verify {
              font-size: 10px !important;
            }
            .generating-pdf .qr-text {
              font-size: 11px !important;
            }
          </style>
          
          <div class="no-print">
            <a href="/other_school/result/list_9" style="padding: 10px 20px; background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 8px; cursor: pointer; font-weight: 600; text-decoration: none; display: inline-flex; align-items: center;">← Back to List</a>
            <button id="printBtn" style="padding: 10px 20px; background: #1e3a8a; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;">🖨️ Print Result</button>
            <button id="downloadPdfBtn" style="padding: 10px 20px; background: #dc2626; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;">📥 Download PDF</button>
          </div>

          <div id="marksheetContainer" class="main-container">
            <!-- Header Pad -->
            <div style="text-align: center; margin-bottom: 12px;">
              <img
                src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/tFCNijuby2oapSCgvrQ8/pub/aT4p92D1bGZMP8sh1wZN.png"
                alt=""
                style="display: block; max-width: 100%; width: 100%; height: auto; margin: 0 auto;"
                onerror="this.src='/pad.png'"
              />
            </div>

            <div class="exam-title-container">
              <div class="exam-title">${escapeHtml(examData.exam_name_year || 'Examination Result')}</div>
            </div>

            <!-- Student Info Meta Grid -->
            <div class="student-name">${escapeHtml(examData.student_name_en || '-')}</div>
            <div class="student-details" style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; border-bottom: 2px dashed #f1f5f9; padding-bottom: 12px;">
              <div><span style="font-weight: 700; color: #64748b;">Father's Name:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.father_name_en || 'N/A')}</span></div>
              <div><span style="font-weight: 700; color: #64748b;">Roll No:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.roll_2025 || '-')}</span></div>
              <div><span style="font-weight: 700; color: #64748b;">Class:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.class_2025 || '-')}</span></div>
              <div><span style="font-weight: 700; color: #64748b;">Section:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(examData.section_2025 || '-')}</span></div>
              <div><span style="font-weight: 700; color: #64748b;">Student ID:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(String(examData.iid || '-'))}</span></div>
              <div><span style="font-weight: 700; color: #64748b;">Mobile:</span> <span style="font-weight: 800; color: #1e293b;">${escapeHtml(mobile || 'N/A')}</span></div>
            </div>

            <!-- Subject Marks Table -->
            <table class="marks-table">
              <thead>
                <tr>
                  <th class="subject-col">Subject</th>
                  <th class="mark-col">${header1}</th>
                  <th class="mark-col">${header2}</th>
                  <th class="mark-col">Practical</th>
                  <th class="mark-col">Total</th>
                  <th class="mark-col">GPA</th>
                </tr>
              </thead>
              <tbody>
                ${subjectsList.map((s, i) => {
                  const borderColors = ['#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f97316', '#a855f7', '#14b8a6'];
                  const leftBorderColor = borderColors[i % borderColors.length];
                  return `
                    <tr>
                      <td class="subject-name" style="border-left: 5px solid ${leftBorderColor};">${escapeHtml(s.subject)}</td>
                      <td>${s.cq || '—'}</td>
                      <td>${s.mcq || '—'}</td>
                      <td>${s.practical || '—'}</td>
                      <td style="font-weight: 800; color: #0f172a;">${s.total || '—'}</td>
                      <td style="font-weight: 800; color: ${s.gpa === 'F' ? '#ef4444' : '#15803d'};">${s.gpa || '—'}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>

            <!-- Summary Bar -->
            <div class="summary-bar">
              <div class="summary-item">
                <div class="summary-label">Total Mark</div>
                <div class="summary-value">${examData.total_mark !== null && examData.total_mark !== undefined ? parseFloat(examData.total_mark).toFixed(1) : '—'}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Avg Mark</div>
                <div class="summary-value">${examData.average_mark !== null && examData.average_mark !== undefined ? parseFloat(examData.average_mark).toFixed(1) : '—'}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">GPA</div>
                <div class="summary-value" style="color: ${gpaFinal === 'F' || gpaFinal === '0' || gpaFinal === '0.00' ? '#ef4444' : '#10b981'};">${gpaFinal}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Remark</div>
                <div class="summary-value" style="color: ${String(remark).toLowerCase().includes('fail') ? '#ef4444' : '#10b981'};">${remark}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Rank</div>
                <div class="summary-value">${classRank}</div>
              </div>
            </div>

            <!-- QR and Footer Section -->
            <div class="qr-section">
              <div style="flex: 1; padding-right: 40px; font-size: 11px; color: #475569; line-height: 1.5;">
                <div style="font-weight: 800; margin-bottom: 6px; color: #1e293b;">নির্দেশনা :</div>
                <p style="margin-bottom: 6px;">
                  নতুন শিক্ষাবর্ষে জানুয়ারির ১ম সপ্তাহে নতুন শ্রেণিতে ভর্তি হয়ে নতুন বই সংগ্রহ করতে হবে। 
                  ভর্তির সময় মার্কশীটের ফটোকপি অবশ্যই আনতে হবে।
                </p>
                <p>
                  গ্রহণযোগ্য কারণ উল্লেখ করে প্রয়োজনীয় ক্ষেত্রে অগ্রিম ছুটি নিতে হবে। 
                  পূর্বানুমতি ছাড়া এক টানা ১০ দিন ক্লাসে অনুপস্থিত থাকলে হাজিরা বহিতে নাম কাটা যাবে।
                </p>
              </div>
              <div style="text-align: center;">
                <div class="qr-box">
                  <a href="${qrURL}" target="_blank" rel="noopener noreferrer">
                    <img src="${qrImgSrc}" alt="QR Code" style="width: 100px; height: 100px;" />
                  </a>
                </div>
                <div class="scan-verify">Scan to Verify</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Bind event listeners for actions
    const printBtn = document.getElementById('printBtn') as HTMLButtonElement;
    const downloadPdfBtn = document.getElementById('downloadPdfBtn') as HTMLButtonElement;

    if (printBtn) {
      printBtn.onclick = () => {
        window.print();
      };
    }

    if (downloadPdfBtn) {
      downloadPdfBtn.onclick = () => {
        // Direct script injection of html2pdf to prevent white-screen error
        const existingScript = document.getElementById('html2pdf-script');
        if (!existingScript) {
          const script = document.createElement('script');
          script.id = 'html2pdf-script';
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
          script.onload = () => runPDF();
          document.body.appendChild(script);
        } else {
          runPDF();
        }
      };
    }

    const runPDF = () => {
      const element = document.getElementById('marksheetContainer');
      if (!element) return;
      
      // @ts-ignore
      const h2p = window.html2pdf;
      if (!h2p) return;

      element.classList.add('generating-pdf');

      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Result_${(examData.student_name_en || 'Student').replace(/\s+/g, '_')}_${examData.iid || ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          letterRendering: true,
          width: 800 // Force width to match the component's maxWidth for consistent scaling
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all' }
      };

      setTimeout(() => {
        h2p().from(element).set(opt).save()
          .then(() => {
            element.classList.remove('generating-pdf');
          })
          .catch((err: unknown) => {
            console.error('PDF generation error:', err);
            element.classList.remove('generating-pdf');
          });
      }, 150);
    };

  } catch (e: any) {
    app.innerHTML = `
      <div style="text-align: center; padding: 60px; color: #d73a49; fontFamily: 'Inter', sans-serif;">
        <p style="font-size: 18px; font-weight: 600; marginBottom: 20px;">❌ Failed to generate report card: ${escapeHtml(e.message)}</p>
        <a href="/other_school/result/list_9" style="display: inline-block; padding: 10px 20px; background: #1e3a8a; color: #fff; text-decoration: none; border-radius: 8px; font-weight: 600;">← Back to List</a>
      </div>
    `;
  }
}
